<template>
  <div v-if="show" class="weight-modal-overlay">
    <div class="weight-modal">
      <div class="modal-header">
        <h2 class="modal-title">
          Enter your
          {{ submissionType === "pre_weight" ? "pre" : "post" }} weight
        </h2>
        <button class="close-btn" @click="handleClose">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="modal-body">
        <form novalidate autocomplete="off" @submit.prevent="handleSubmit">
          <!-- Weight Input -->
          <div class="form-group">
            <label for="weight-input" class="form-label">Weight<span class="text-danger">*</span></label>
            <div class="weight-input-container">
              <input
                id="weight-input"
                type="number"
                @wheel="$event.target.blur()"
                v-model.number="weightValue"
                class="weight-input"
                :class="{ error: errors.weight }"
                placeholder="Enter weight"
                step="0.1"
                min="1"
                max="300"
                required
                @blur="validateWeight"
                @input="clearWeightError"
              />
              <span class="weight-unit">kg</span>
            </div>
            <div v-if="errors.weight" class="error-message">
              {{ errors.weight }}
            </div>
          </div>

          <!-- Video Link Input -->
          <div class="form-group">
            <label for="video-input" class="form-label">Video Link<span class="text-danger">*</span></label>
            <input
              id="video-input"
              type="url"
              v-model="videoLink"
              class="video-input"
              :class="{ error: errors.videoLink }"
              placeholder="https://www.youtube.com/"
              required
              @blur="validateVideoLink"
              @input="clearVideoLinkError"
            />
            <div v-if="errors.videoLink" class="error-message">
              {{ errors.videoLink }}
            </div>
          </div>

          <div
            v-for="field in additionalFields"
            :key="field.key"
            class="form-group"
            :class="{ 'has-instruction': field.instruction }"
          >
            <label :for="field.key" class="form-label">{{ field.placeholder }}<span v-if="field.required !== false" class="text-danger">*</span></label>
            <input
              :id="field.key"
              type="text"
              v-model="additionalFieldValues[field.key]"
              class="video-input"
              :class="{ error: additionalFieldErrors[field.key] }"
              :placeholder="field.placeholder"
              maxlength="255"
              :required="field.required !== false"
              @input="additionalFieldErrors[field.key] = ''"
              @blur="additionalFieldErrors[field.key] = additionalFieldError(field)"
            />
            <div v-if="field.instruction" class="field-instruction">
              {{ field.instruction }}
            </div>
            <div v-if="additionalFieldErrors[field.key]" class="error-message">
              {{ additionalFieldErrors[field.key] }}
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="modal-actions">
            <button type="button" class="cancel-btn" @click="handleClose">
              Cancel
            </button>
            <button type="submit" class="submit-btn" :disabled="isSubmitting">
              {{ isSubmitting ? "Submitting..." : "Submit" }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "WeightSubmissionModal",
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    submissionType: {
      type: String,
      default: "pre_weight", // 'pre_weight' or 'post_weight'
    },
    eventData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      weightValue: null, // Changed to null for better number handling
      videoLink: "",
      isSubmitting: false,
      errors: {
        weight: "",
        videoLink: "",
      },
      additionalFieldValues: {},
      additionalFieldErrors: {},
    };
  },
  computed: {
    additionalFields() {
      return this.eventData.additional_fields || [];
    },
  },
  watch: {
    show(newVal) {
      if (newVal) {
        // Reset form when modal opens
        this.resetForm();
      }
    },
  },
  methods: {
    resetForm() {
      this.weightValue = null;
      this.videoLink = "";
      this.isSubmitting = false;
      this.errors = {
        weight: "",
        videoLink: "",
      };
      this.additionalFieldValues = this.emptyAdditionalFields();
      this.additionalFieldErrors = this.emptyAdditionalFields();
    },

    emptyAdditionalFields() {
      return Object.fromEntries(this.additionalFields.map((f) => [f.key, ""]));
    },

    additionalFieldError(field) {
      return field.required === false || this.additionalFieldValues[field.key]?.trim() ? "" : "This field is required";
    },

    validateAdditionalFields() {
      this.additionalFieldErrors = Object.fromEntries(
        this.additionalFields.map((f) => [f.key, this.additionalFieldError(f)])
      );
      return Object.values(this.additionalFieldErrors).every((e) => !e);
    },

    // Individual validation methods for real-time feedback
    validateWeight() {
      this.errors.weight = "";

      if (!this.weightValue || this.weightValue <= 0) {
        this.errors.weight = "Please enter a valid weight";
        return false;
      } else if (this.weightValue < 20 || this.weightValue > 300) {
        this.errors.weight = "Weight must be between 20-300 kg";
        return false;
      }
      return true;
    },

    validateVideoLink() {
      this.errors.videoLink = "";

      if (!this.videoLink || this.videoLink.trim() === "") {
        this.errors.videoLink = "Please enter a valid video link";
        return false;
      } else if (!this.isValidUrl(this.videoLink)) {
        this.errors.videoLink = "Please enter a valid URL";
        return false;
      }
      return true;
    },

    // Clear error methods for better UX
    clearWeightError() {
      if (this.errors.weight) {
        this.errors.weight = "";
      }
    },

    clearVideoLinkError() {
      if (this.errors.videoLink) {
        this.errors.videoLink = "";
      }
    },

    validateForm() {
      // Reset all errors first
      this.errors = {
        weight: "",
        videoLink: "",
      };

      let isValid = true;

      // Validate weight
      if (!this.validateWeight()) {
        isValid = false;
      }

      // Validate video link
      if (!this.validateVideoLink()) {
        isValid = false;
      }

      if (!this.validateAdditionalFields()) {
        isValid = false;
      }

      return isValid;
    },

    isValidUrl(string) {
      try {
        new URL(string);
        return true;
      } catch (error) {
        console.error("Invalid URL:", error);
        return false;
      }
    },

    async handleSubmit() {
      if (!this.validateForm()) {
        return;
      }

      this.isSubmitting = true;

      const submissionData = {
        type: this.submissionType,
        weight: parseFloat(this.weightValue),
        video_link: this.videoLink.trim(),
        event_id: this.eventData.event_id,
        web_event_participant_id: this.eventData.web_event_participant_id,
        day_number: this.eventData.day_number || 1,
        additional_fields: Object.fromEntries(
          Object.entries(this.additionalFieldValues).map(([key, value]) => [
            key,
            value.trim(),
          ])
        ),
        // event_name: this.eventData.event_name,
        //category: this.eventData.category,
        // submitted_at: new Date().toISOString(),
      };

      try {
        // Emit submit event with the data
        this.$emit("submit", submissionData);

        // Don't close modal here - let parent handle success/error
        // this.handleClose();
      } catch (error) {
        console.error("Weight submission error:", error);
        this.isSubmitting = false;
      }
    },

    handleClose() {
      this.resetForm();
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.form-group.has-instruction {
  margin-bottom: 18px;
}

.field-instruction {
  margin-top: 6px;
  font-size: 12px;
  color: #666;
}

.modal-actions .cancel-btn,
.modal-actions .submit-btn {
  padding: 12px 24px;
  font-size: 14px;
  line-height: 1.2;
}

.modal-actions .submit-btn {
  max-width: none;
  border: 2px solid transparent;
  box-shadow: none;
}

.weight-input.error,
.video-input.error {
  margin-top: 0;
  text-align: left;
}

@media (max-width: 768px) {
  .modal-actions {
    flex-direction: column-reverse;
  }
}

@media (max-width: 576px) {
  .weight-modal-overlay {
    padding: 12px;
  }

  .weight-modal {
    margin: 0;
    max-height: calc(100vh - 24px);
  }

  .modal-header {
    padding: 16px 16px 12px;
  }

  .modal-body {
    padding: 16px;
  }

  .modal-title {
    font-size: 17px;
  }

  .modal-actions {
    margin-top: 24px;
  }
}
</style>
